import { useFragment, graphql } from "react-relay";
import {
  SinglePeriodMetric,
  SinglePeriodTable$key,
} from "./__generated__/SinglePeriodTable.graphql";

import { AHIcon, AHSeparator, AHText, Box, TouchableBox, useTheme } from "arkh";

import React, { useCallback, useMemo, useState } from "react";
import { MultiGrid } from "react-virtualized";
import { PillIndicator } from "../PillIndicator";

import { useFormatValueUnit } from "./formatDuration";
import { useMetricToLabel } from "./metricToLabel";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { Platform, View } from "react-native";

const CELL_WIDTH = 140;
const FIRST_COLUMN_WIDTH = 180;
const CELL_HEIGHT = 40;
const FIRST_ROW_HEIGHT = 70;

function OrderIcon(props: { order: "asc" | "desc" }) {
  return (
    <Box
      style={{
        opacity: props.order === null ? 0 : 1,
        transform: [{ translateY: props.order === null ? -5 : 0 }],
        ...(Platform.OS === "web"
          ? ({ transition: ".2s transform" } as any)
          : {}),
      }}
      spaceLeft="xxsmall"
    >
      <View
        style={{
          ...(Platform.OS === "web"
            ? ({ transition: ".3s transform" } as any)
            : {}),
          transform: [{ rotate: props.order === "asc" ? "180deg" : "0deg" }],
        }}
      >
        <AHIcon size="xxsmall" type="arrow-down" color={"text-primary"} />
      </View>
    </Box>
  );
}
export function SinglePeriodTable(props: {
  data: SinglePeriodTable$key;
  height: number;
  width: number;
  searchQuery?: string;
}) {
  const data = useFragment(
    graphql`
      fragment SinglePeriodTable on SinglePeriodAnalytics {
        metrics {
          hasAvg
          hasTotal
          metric
          valueUnit
        }
        dimensions {
          label
          metrics {
            metric
            avg
            total
          }
        }
      }
    `,
    props.data
  );

  const METRICS = data.metrics;
  const _dimensions = useMemo(
    () =>
      props.searchQuery && props.searchQuery.length > 0
        ? data.dimensions.filter((d) =>
            d.label.toLowerCase().includes(props.searchQuery!.toLowerCase())
          )
        : data.dimensions,
    [data.dimensions, props.searchQuery]
  );

  const oddColor = useTheme()["surface-background-selected"];

  const metricToLabel = useMetricToLabel();
  const formatValueUnit = useFormatValueUnit();
  const intl_tokens = useIntlTokens();

  const [sortBy, setSortBy] = useState<{
    metric: SinglePeriodMetric;
    field: "avg" | "total";
    order: "asc" | "desc";
  }>({
    metric: "ENGAGEMENT",
    field: "avg",
    order: "desc",
  });

  const onToggleSort = useCallback(
    (metric: SinglePeriodMetric, field: "avg" | "total") => {
      setSortBy((prev) => {
        if (prev.metric === metric && prev.field === field) {
          return {
            ...prev,
            order: prev.order === "asc" ? "desc" : "asc",
          };
        } else {
          return {
            ...prev,
            metric,
            field,
          };
        }
      });
    },
    []
  );

  const onToggleSortMetric = useCallback(
    (metric: SinglePeriodMetric, defaultField: "avg" | "total") => {
      setSortBy((prev) => {
        if (prev.metric === metric) {
          return { ...prev, order: prev.order === "asc" ? "desc" : "asc" };
        }
        return { metric, field: defaultField, order: "desc" };
      });
    },
    []
  );

  const sortedDimensions = useMemo(() => {
    return _dimensions.slice().sort((a, b) => {
      const aValue =
        (sortBy.field === "avg"
          ? a.metrics.find((m) => m.metric === sortBy.metric)?.avg
          : a.metrics.find((m) => m.metric === sortBy.metric)?.total) ?? 0;
      const bValue =
        (sortBy.field === "avg"
          ? b.metrics.find((m) => m.metric === sortBy.metric)?.avg
          : b.metrics.find((m) => m.metric === sortBy.metric)?.total) ?? 0;
      if (aValue === bValue) {
        return 0;
      }
      return sortBy.order === "asc" ? aValue - bValue : bValue - aValue;
    });
  }, [_dimensions, sortBy]);

  return (
    <>
      <AHSeparator />
      <Box color="surface" spaceHorizontal="medium">
        <Box style={{ height: props.height, width: props.width }}>
          <MultiGrid
            rowCount={sortedDimensions.length + 1}
            columnCount={METRICS.length + 1}
            columnWidth={(index) => {
              if (index.index === 0) {
                return FIRST_COLUMN_WIDTH;
              }
              return CELL_WIDTH;
            }}
            height={props.height}
            enableFixedColumnScroll
            enableFixedRowScroll
            fixedColumnCount={1}
            fixedRowCount={1}
            rowHeight={({ index }) => {
              if (index === 0) {
                return FIRST_ROW_HEIGHT;
              }
              return CELL_HEIGHT;
            }}
            hideTopRightGridScrollbar
            hideBottomLeftGridScrollbar
            width={props.width}
            cellRenderer={({ columnIndex, rowIndex, style: s }) => {
              const metric = METRICS[columnIndex - 1];

              const isOddColumn = columnIndex % 2 === 0;

              if (rowIndex === 0 && columnIndex !== 0) {
                const [label, subLabel] = metricToLabel(metric.metric);
                return (
                  <div
                    style={{
                      ...s,

                      display: "flex",
                      flexDirection: "column" as const,
                      backgroundColor:
                        isOddColumn && columnIndex > 1 ? oddColor : undefined,
                    }}
                  >
                    <TouchableBox
                      onPress={() => {
                        if (metric.hasTotal) {
                          onToggleSortMetric(metric.metric, "total");
                        } else if (metric.hasAvg) {
                          onToggleSortMetric(metric.metric, "avg");
                        }
                      }}
                    >
                      <AHText
                        spaceLeft="medium"
                        type="info-small"
                        color="darkerText"
                        spaceTop="medium"
                      >
                        {label}
                        {metric.metric === "ENGAGEMENT" &&
                        sortBy.metric === "ENGAGEMENT" ? (
                          <OrderIcon order={sortBy.order} />
                        ) : null}
                      </AHText>
                      {subLabel ? (
                        <AHText spaceLeft="medium" type="detail">
                          {subLabel}
                        </AHText>
                      ) : null}
                    </TouchableBox>
                    {columnIndex > 1 ? (
                      <Box style={{ flexDirection: "row" }} spaceTop="medium">
                        {metric.hasTotal ? (
                          <TouchableBox
                            onPress={() => onToggleSort(metric.metric, "total")}
                          >
                            <AHText
                              style={{ width: CELL_WIDTH / 2 }}
                              type="info-small"
                              color="darkText"
                              spaceLeft="medium"
                            >
                              {intl_tokens.Total}
                              {sortBy.metric === metric.metric &&
                              sortBy.field === "total" ? (
                                <OrderIcon order={sortBy.order} />
                              ) : null}
                            </AHText>
                          </TouchableBox>
                        ) : null}
                        {metric.hasAvg ? (
                          <TouchableBox
                            onPress={() => onToggleSort(metric.metric, "avg")}
                          >
                            <AHText
                              spaceLeft="medium"
                              style={{ width: CELL_WIDTH / 2 }}
                              type="detail"
                            >
                              {intl_tokens.Avg}{" "}
                              {sortBy.metric === metric.metric &&
                              sortBy.field === "avg" ? (
                                <OrderIcon order={sortBy.order} />
                              ) : null}
                            </AHText>
                          </TouchableBox>
                        ) : null}
                      </Box>
                    ) : null}
                  </div>
                );
              }

              const dimension = sortedDimensions[rowIndex - 1];

              const style = {
                ...s,

                display: "flex",
                flexDirection: "row" as const,
                alignItems: "center",
              };

              if (columnIndex === 0) {
                return (
                  <div style={style}>
                    <Box
                      spaceLeft="medium"
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        flex: 1,
                      }}
                    >
                      <AHText
                        spaceLeft="small"
                        numberOfLines={1}
                        type="subtitle"
                      >
                        {dimension?.label}
                      </AHText>
                    </Box>
                  </div>
                );
              }

              const metricValue = dimension?.metrics.find(
                (m) => m.metric === metric.metric
              );
              if (metric.metric === "ENGAGEMENT") {
                return (
                  <div style={style}>
                    <AHText style={{ flex: 1 }} numberOfLines={1}>
                      <PillIndicator
                        percentage={(metricValue?.avg ?? 0) * 100}
                      />
                    </AHText>
                  </div>
                );
              }

              return (
                <div
                  style={{
                    ...style,
                    backgroundColor: isOddColumn ? oddColor : undefined,
                  }}
                >
                  {metric.hasTotal ? (
                    <AHText
                      style={{ flex: 1 }}
                      numberOfLines={1}
                      color="darkerText"
                      spaceLeft="medium"
                    >
                      {formatValueUnit(metricValue?.total, metric.valueUnit)}
                    </AHText>
                  ) : null}
                  {metric.hasAvg ? (
                    <AHText
                      spaceLeft="medium"
                      style={{ flex: 1 }}
                      numberOfLines={1}
                    >
                      {formatValueUnit(metricValue?.avg, metric.valueUnit)}
                    </AHText>
                  ) : null}
                </div>
              );
            }}
          />
        </Box>
      </Box>
    </>
  );
}
